import React, { useEffect, useState } from 'react';
import './Item.scss';
import { filter, find, findIndex, forEach, isNil, map } from 'lodash-es';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { Form, FormikProvider, useFormik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import Lottie from 'lottie-react';
import loader from '../assets/lotties/loader.json';
import 'react-spring-bottom-sheet/dist/style.css';
import { useGetMenuItemByIdQuery } from '../store/menu/menuItem/menuItemApi';
import { ICreateOrderItemFromMenuItemRequest } from '../proto/order/orderItem';
import { ensureDataUrl } from '../utils/imageHandlers';
import { ItemPrice } from './ItemPrice/ItemPrice';

interface IMenuItemProps {
	menuItemId: string;
	onDismiss?: () => void;
	onSubmit: (values: ICreateOrderItemFromMenuItemRequest) => void;
}

export const Item = ({ menuItemId, onDismiss, onSubmit }: IMenuItemProps): JSX.Element => {
	const { data: menuItem, isLoading: isLoadingMenuItem } = useGetMenuItemByIdQuery(menuItemId, { skip: isNil(menuItemId) });
	const [open, setOpen] = useState(false);

	const handleMenuITemFormSubmit = (values: ICreateOrderItemFromMenuItemRequest): void => {
		onSubmit(values);
	};

	const formik = useFormik<ICreateOrderItemFromMenuItemRequest>({
		initialValues: {
			menuItemId: menuItem?.id,
			quantity: 1,
			customizationGroups: map(menuItem?.customizationGroups, (group) => ({
				menuItemCustomizationGroupId: group.id,
				selections: [],
			})),
		},
		onSubmit: handleMenuITemFormSubmit,
		enableReinitialize: false,
		validationSchema: null,
	});

	const { resetForm, values, setFieldValue, handleSubmit, getFieldMeta } = formik;

	useEffect(() => {
		resetForm({
			values: {
				menuItemId: menuItem?.id,
				quantity: 1,
				customizationGroups: map(menuItem?.customizationGroups, (group) => ({
					menuItemCustomizationGroupId: group.id,
					selections: [],
				})),
			},
		});
	}, [menuItem, resetForm]);

	useEffect(() => {
		if (!isNil(menuItemId)) {
			setOpen(true);
		} else {
			setOpen(false);
		}
	}, [menuItemId]);

	const onDismissSheet = (): void => {
		setOpen(false);
		onDismiss();
	};

	const isOptionSelected = (customizationGroupId: string, optionId: string): boolean => {
		const customizationGroup = find(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);

		return !isNil(find(customizationGroup?.selections, (selection) => selection.customizationGroupOptionId === optionId));
	};

	const handleOptionClick = (customizationGroupId: string, optionId: string): void => {
		const isSelected = isOptionSelected(customizationGroupId, optionId);
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);
		let newSelections = values.customizationGroups[customizationGroupIndex]?.selections || [];

		if (isSelected) {
			// Deselect the option
			newSelections = filter(newSelections, (selection) => selection.customizationGroupOptionId !== optionId);
		} else {
			newSelections = [...newSelections, { customizationGroupOptionId: optionId, quantity: 1 }];
		}

		setFieldValue(`customizationGroups[${customizationGroupIndex}].selections`, newSelections);
	};

	const getSelectionQuantity = (customizationGroupId: string, optionId: string): number => {
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);
		const matchingSelection = find(
			values.customizationGroups[customizationGroupIndex]?.selections,
			(selection) => selection.customizationGroupOptionId === optionId
		);

		return matchingSelection?.quantity ?? 0;
	};

	const isIncreaseAvailable = (customizationGroupId: string): boolean => {
		const customizationGroup = find(menuItem?.customizationGroups, (group) => group.id === customizationGroupId);
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);

		let currentSelectedOptionsInGroup = 0;

		forEach(values.customizationGroups[customizationGroupIndex].selections, (selection) => {
			currentSelectedOptionsInGroup += selection.quantity;
		});

		return currentSelectedOptionsInGroup < customizationGroup.max;
	};

	const isSubtractAvailable = (customizationGroupId: string): boolean => {
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);

		let currentSelectedOptionsInGroup = 0;

		forEach(values.customizationGroups[customizationGroupIndex].selections, (selection) => {
			currentSelectedOptionsInGroup += selection.quantity;
		});

		return currentSelectedOptionsInGroup > 0;
	};

	const handleIncreaseConfigQuantity = (customizationGroupId: string, optionId: string): void => {
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);
		const selections = values.customizationGroups[customizationGroupIndex]?.selections ?? [];
		const selectionIndex = findIndex(selections, (selection) => selection.customizationGroupOptionId === optionId);

		if (selectionIndex === -1) {
			setFieldValue(`customizationGroups[${customizationGroupIndex}].selections`, [...selections, { customizationGroupOptionId: optionId, quantity: 1 }]);
		} else {
			setFieldValue(`customizationGroups[${customizationGroupIndex}].selections[${selectionIndex}].quantity`, selections[selectionIndex].quantity + 1);
		}
	};

	const handleSubtractConfigQuantity = (customizationGroupId: string, optionId: string): void => {
		const customizationGroupIndex = findIndex(values.customizationGroups, (group) => group.menuItemCustomizationGroupId === customizationGroupId);
		const selections = values.customizationGroups[customizationGroupIndex]?.selections ?? [];
		const selectionIndex = findIndex(selections, (selection) => selection.customizationGroupOptionId === optionId);

		setFieldValue(`customizationGroups[${customizationGroupIndex}].selections[${selectionIndex}].quantity`, selections[selectionIndex].quantity - 1);
	};

	const hasError = (fieldPath: string): boolean => {
		return getFieldMeta(fieldPath).touched && !isNil(getFieldMeta(fieldPath).error);
	};

	return (
		<div>
			{!isNil(menuItem) && (
				<BottomSheet
					open={open}
					onDismiss={onDismissSheet}
					defaultSnap={({ snapPoints, lastSnap }): any => lastSnap ?? Math.min(...snapPoints)}
					snapPoints={({ maxHeight }): any => [maxHeight - maxHeight / 5, maxHeight]}
					header={<div>{menuItem.title}</div>}
					className="bottom-sheet-container"
					expandOnContentDrag={false}
				>
					{isLoadingMenuItem ? (
						<Lottie animationData={loader} loop style={{ width: '10rem' }} />
					) : (
						<FormikProvider value={formik}>
							<Form>
								<div className="bottom-sheet-content">
									<img src={ensureDataUrl(menuItem.image) as string} alt="title" />
									<div className="title">{menuItem.title}</div>
									<div className="price">{menuItem.price} ₪</div>
									<div className="description">{menuItem.description}</div>
									<div className="customizationGroups">
										{map(menuItem.customizationGroups, (customizationGroup, groupIndex) => (
											<div className="customizationGroup" key={customizationGroup.id}>
												<div className="customizationGroupTitle">{customizationGroup.title}</div>
												{hasError(`customizationGroups.${groupIndex}`) && (
													<div className="customizationGroupError">{getFieldMeta(`customizationGroups.${groupIndex}`).error}</div>
												)}
												<div className="customizationGroupOptions">
													{map(customizationGroup.options, (option) => (
														<div className="customizationGroupOption" key={option.id}>
															<div
																className="customizationGroupOptionSelection"
																onClick={(): void => handleOptionClick(customizationGroup.id, option.id)}
															>
																<div
																	className={classNames('customizationGroupOptionSelectionCheckbox', {
																		active: isOptionSelected(customizationGroup.id, option.id),
																	})}
																>
																	<div className="selectionCheckboxIcon">
																		<FontAwesomeIcon icon={faCheck} />
																	</div>
																</div>
																<div className="customizationGroupOptionSelectionTitle">{option.name}</div>
															</div>
															<div className="customizationGroupOptionControls">
																{isOptionSelected(customizationGroup.id, option.id) && customizationGroup.max > 1 && (
																	<div className="customizationGroupOptionQuantity">
																		{isIncreaseAvailable(customizationGroup.id) && (
																			<div
																				className="customizationGroupOptionQuantityAdd"
																				onClick={(): void =>
																					handleIncreaseConfigQuantity(customizationGroup.id, option.id)
																				}
																			>
																				+
																			</div>
																		)}
																		<div className="customizationGroupOptionQuantityValue">
																			{getSelectionQuantity(customizationGroup.id, option.id)}
																		</div>
																		{isSubtractAvailable(customizationGroup.id) && (
																			<div
																				className="customizationGroupOptionQuantitySubtract"
																				onClick={(): void =>
																					handleSubtractConfigQuantity(customizationGroup.id, option.id)
																				}
																			>
																				-
																			</div>
																		)}
																	</div>
																)}

																<div className="optionQuantityPriceAddonContainer">
																	{!isNil(option.priceAddOn) && option.priceAddOn > 0 && (
																		<div className="optionQuantityPriceAddon">+ {option.priceAddOn} ₪</div>
																	)}
																</div>
															</div>
														</div>
													))}
												</div>
											</div>
										))}
									</div>
									<div className="bottom-sheet-footer-transparent" />
									<div className="bottom-sheet-footer">
										<div className="order-button" onClick={(): void => handleSubmit()}>
											<span className="add-to-order">הוסף להזמנה</span>
											<ItemPrice item={values} menuItem={menuItem} />
										</div>
									</div>
								</div>
							</Form>
						</FormikProvider>
					)}
				</BottomSheet>
			)}
		</div>
	);
};
